import { Link, useNavigate, useParams } from "react-router-dom"
import useTargetStorage from "@/hooks/useTargetStorage"
import DeletedDocCallout from "@/components/callout/deletedDocCallout"
import { useTranslation } from "react-i18next"
import useLocations from "hooks/useLocations"
import ProductsList from "@/features/storages/products/productList"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import Section from "components/layout/section"
import SectionHeader from "components/layout/sectionHeader"
import SectionHeaderTitle from "components/layout/sectionHeaderTitle"
import SectionDivider from "components/layout/sectionDivider"
import RightSidebarLocation from "components/sidebar/right/RightSidebarLocation"
import Button from "components/inputs/button"
import LocationTags from "components/tags/locationTags"
import SectionHeaderActions from "components/layout/sectionHeaderActions"
import PageTitle from "features/navigation/pageTitle"
import useAccess from "@/hooks/useAccess"
import { AccessRole, FormStatus, DeviceSize } from "@/types"
import useStorages from "@/hooks/useStorages"
import { paramGuardLocation, paramGuardStorage } from "@/utils/paramGuard"
import FloatingButtonGroup from "@/components/buttons/floatingButtonGroup"
import FloatingButtonGroupButton from "@/components/buttons/FloatingButtonGroupButton"
import FloatingButtonGroupHr from "@/components/buttons/FloatingButtonGroupHr"
import useLayout from "@/hooks/useLayout"
import IconButton from "@/components/buttons/iconButton"
import { ReactComponent as MoreVertIcon } from "assets/icons/more_vert.svg"
import useWindowDimensions from "@/hooks/useWindowDimensions"
import { useState } from "react"
import TableList from "@/components/list/tableList/tableList"
import { MainSheetHeaderActions } from "@/components/layout/layoutChrome"
import TableListRow from "@/components/list/tableList/tableListRow"
import TableListCell from "@/components/list/tableList/tableListCell"

const LocationPage = () => {

    const { t } = useTranslation()
    const navigate = useNavigate()
    const { displayIfSize } = useWindowDimensions()

    const { targetStorageDocId: storageId } = useTargetStorage()
    const { locationId } = useParams<{ locationId: string }>()
    
    const {
        limitToRoles
    } = useAccess({ storageId: storageId || '' })

    const {
        location,
        locationStatus,
        locationsInitialized,
        allLocations,
        restoreLocationAsync,
        isRestoring
    } = useLocations({ storageId, locationId })

    const {
        storage,
        storageStatus,
    } = useStorages({ storageId })

    const { openDialog, addToast } = useLayout()
    const [isRestoringState, setIsRestoringState] = useState(false)

    const isLoading = storageStatus === FormStatus.LOADING || !location?.name;

    const getLocationPath = (id: string): string[] => {
        const path: string[] = []
        let current = allLocations.find(c => c && c.id === id)
        while (current && current.relations?.parentLocation) {
            path.unshift(current.relations.parentLocation)
            current = allLocations.find(c => c && c.id === current?.relations?.parentLocation)
        }
        return path
    }

    const getChildLocationsIdArray = (id: string): string[] => {
        return allLocations.filter(c => c && c.relations?.parentLocation === id).map(c => c.id!).filter(Boolean)
    }

    const locationIdArrayToNameArray = (ids: string[]): string[] => {
        return ids.map(id => {
            const c = allLocations.find(c => c && c.id === id)
            return c ? c.name : ''
        }).filter(Boolean)
    }

    if (
        !paramGuardStorage({storageId, storage, storageStatus, navigate}) ||
        !paramGuardLocation({storageId, locationId, location, locationStatus, navigate})
    ) {
        return <></>
    }
    const safeStorageId = storageId!;
    const safeLocationId = locationId!;

    const handleRestoreClick = async () => {
        setIsRestoringState(true)
        try {
            await restoreLocationAsync({
                storageId: safeStorageId,
                locationId: safeLocationId
            })
            addToast({
                text: t('locationRestored'),
                colorScheme: 'green'
            })
        } catch (error) {
            addToast({
                text: t('failedToRestore'),
                colorScheme: 'red'
            })
        } finally {
            setIsRestoringState(false)
        }
    }

    return (
        <>
            <PageTitle title={location?.name ? `${t('titleLocation')}: ${location.name}` : t('titleLocation')} />
            <MainSheetHeaderActions>
                {displayIfSize([DeviceSize.SM]) && locationsInitialized && !location?.isDeleted && limitToRoles([AccessRole.OWNER, AccessRole.EDITOR]) &&
                    <FloatingButtonGroup id={`storage_edit_${storageId}`} openingButton={<IconButton icon={<MoreVertIcon />} />}>
                        <FloatingButtonGroupButton>
                            <Link to={`/storage/locations/${locationId}/edit`}>{t('editLocation')}</Link>
                        </FloatingButtonGroupButton>
                        <FloatingButtonGroupHr />
                        <FloatingButtonGroupButton>
                            <Link
                                to={`/storage/locations/create`}
                                state={{
                                    locationId: locationId,
                                }}
                            >{t('buttonNewChildLocation')}</Link>
                        </FloatingButtonGroupButton>
                        <FloatingButtonGroupHr />
                        <FloatingButtonGroupButton onClick={() => openDialog(`delete_location`, {storageId, locationId})}>
                            {t('delete')}
                        </FloatingButtonGroupButton>
                    </FloatingButtonGroup>
                }
            </MainSheetHeaderActions>
            {location?.isDeleted && (
                <DeletedDocCallout
                    documentType="location"
                    deletionType="hard"
                    action={handleRestoreClick}
                    isRestoring={isRestoring || isRestoringState}
                    deletedAt={location?.keyHistory?.deletedAt}
                />
            )}
            <Section>
                {location && location.relations?.parentLocation && <LocationTags
                    label={t(`labelLocationPath`)}
                    locationNames={locationIdArrayToNameArray(getLocationPath(safeLocationId))}
                    locationIds={getLocationPath(safeLocationId)}
                    storageId={safeStorageId}
                />}
                {location && getChildLocationsIdArray(safeLocationId).length > 0 &&
                    <LocationTags
                        label={t(`labelChildLocations`)}
                        locationNames={locationIdArrayToNameArray(getChildLocationsIdArray(safeLocationId))}
                        locationIds={getChildLocationsIdArray(safeLocationId)}
                        separator=''
                        storageId={safeStorageId}
                    />
                }

                <TableList width="fit">
                    {!isLoading ?
                        <>
                            <TableListRow hoverEffect={false}>
                                <TableListCell isLabel={true}><b>{t('labelDescription')}</b></TableListCell>
                                <TableListCell>{location?.description || '-'}</TableListCell>
                            </TableListRow>
                            <TableListRow hoverEffect={false}>
                                <TableListCell isLabel={true}><b>{t('labelNotes')}</b></TableListCell>
                                <TableListCell>{location?.notes || '-'}</TableListCell>
                            </TableListRow>
                        </>
                        :
                        <>
                            {Array.from({ length: 2 }).map((_, _index) => (
                                <TableListRow key={_index} hoverEffect={false}>
                                    <TableListCell><p className="skeleton shortest"></p></TableListCell>
                                    <TableListCell><p className="skeleton short"></p></TableListCell>
                                </TableListRow>
                            ))}
                        </>
                    }
                </TableList>
            </Section>
            
            <SectionDivider />
            
            <Section padX={false}>
                <SectionHeader>
                    <SectionHeaderTitle>
                        <h2>{t(`titleProducts`)}</h2>
                    </SectionHeaderTitle>
                    <SectionHeaderActions>
                        { limitToRoles([AccessRole.OWNER, AccessRole.EDITOR]) && !location?.isDeleted &&
                            <>
                                {!displayIfSize([DeviceSize.SM]) && <Button wideOnMobile
                                    to={`/storage/products/create`}
                                    state={{
                                        locationId: locationId,
                                    }}
                                    design={`primary`}>
                                    <AddIcon />
                                    <span>{t('buttonNewProduct')}</span>
                                </Button>}
                                {displayIfSize([DeviceSize.SM]) && <IconButton
                                    icon={<AddIcon />}
                                    colorScheme="blue"
                                    to={`/storage/products/create`}
                                    state={{
                                        locationId: locationId,
                                    }}
                                />}
                            </>
                        }
                    </SectionHeaderActions>
                </SectionHeader>
                <ProductsList storageId={safeStorageId} locationId={safeLocationId} displayHelper={!location?.isDeleted} enableActions={!location?.isDeleted} />
            </Section>
            <RightSidebarLocation locationId={safeLocationId} storageId={safeStorageId} />
        </>
    )
}

export default LocationPage
